import type React from 'react';

import type { Body, Meta, Uppy } from '@uppy/core';

import { Button, Text } from '@change/ds-core-ui';
import { Form } from '@change/ds-forms';
import { InfoV2Icon } from '@change/ds-icons';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { EndorsementCardEdit, EndorsementModal } from '#src/app/shared/components/endorsements';
import type { EndorsementFormData } from '#src/app/shared/components/endorsements';
import type { RawMediaCropInput, UploaderMethodProps } from '#src/app/shared/components/imageUpload';

import { endorsementFormSchema, type EndorsementFormSchema } from '../../shared/validation';
import { useEndorsementFocus } from '../hooks/useEndorsementFocus';

import { EndorsementFormFields } from './Fields';
import { EndorsementFormFooter } from './Footer';
import { EndorsementFormHeader } from './Header';

type PreviewSectionProps = {
	formData: EndorsementFormSchema;
	image: string | undefined;
	croppedPreviewUrl?: string;
	focusedField: ReturnType<typeof useEndorsementFocus>['focusedField'];
	previewSection: string;
	onHelpClick: () => void;
};

const PreviewSection: React.FC<PreviewSectionProps> = ({
	formData,
	image,
	croppedPreviewUrl,
	focusedField,
	previewSection,
	onHelpClick,
}) => {
	const { translate } = useI18n();

	const namePlaceholder = translate('corgi.petition.starter_dashboard_v2.endorsements.add.preview_name_placeholder');
	const titlePlaceholder = translate('corgi.petition.starter_dashboard_v2.endorsements.add.preview_title_placeholder');
	const descriptionPlaceholder = translate(
		'corgi.petition.starter_dashboard_v2.endorsements.add.preview_description_placeholder',
	);

	const displayName = formData.name || namePlaceholder;
	const displayTitle = formData.title || titlePlaceholder;
	const displayDescription = formData.statement || descriptionPlaceholder;

	return (
		<div className={previewSection}>
			<Text variant="body" fontWeight="bold" className="text-typography-strong mb-4">
				<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.preview_heading" />
			</Text>
			<EndorsementCardEdit
				name={displayName}
				title={displayTitle}
				description={displayDescription}
				photoUrl={croppedPreviewUrl || image || formData.photoUrl || ''}
				link={formData.url}
				focusedField={focusedField}
				displaySeparator
				isNamePlaceholder={!formData.name}
				isTitlePlaceholder={!formData.title}
				isDescriptionPlaceholder={!formData.statement}
			/>

			<Button
				onClick={onHelpClick}
				data-testid="endorsement-help-link"
				data-qa="endorsement-help-link"
				size="small"
				variant="tertiary"
				leadingIcon={<InfoV2Icon className="text-typography-strong" size="medium" />}
				className="mt-6 hidden w-fit items-start gap-2 pr-1 pl-0 lg:flex"
			>
				<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.view_endorsement_example" />
			</Button>
		</div>
	);
};

type ModalsProps = {
	isHelpModalOpen: boolean;
	isSubmitModalOpen: boolean;
	isSubmitting: boolean;
	onCloseHelpModal: () => void;
	onCloseSubmitModal: () => void;
	onConfirmSubmit: () => void | Promise<void>;
	formData: EndorsementFormSchema;
	isTermsAgreed: boolean;
	onTermsChange: (checked: boolean) => void;
	croppedPreviewUrl?: string;
};

const Modals: React.FC<ModalsProps> = ({
	isHelpModalOpen,
	isSubmitModalOpen,
	isSubmitting,
	onCloseHelpModal,
	onCloseSubmitModal,
	onConfirmSubmit,
	formData,
	isTermsAgreed,
	onTermsChange,
	croppedPreviewUrl,
}) => (
	<>
		<EndorsementModal
			variant="help"
			isOpen={isHelpModalOpen}
			onClose={onCloseHelpModal}
			formData={{ ...formData, statement: formData.statement || '' }}
		/>
		<EndorsementModal
			variant="submit"
			isOpen={isSubmitModalOpen}
			isSubmitting={isSubmitting}
			onClose={onCloseSubmitModal}
			onConfirm={onConfirmSubmit}
			formData={{
				...formData,
				statement: formData.statement || '',
				photoUrl: croppedPreviewUrl || formData.photoUrl || '',
			}}
			isTermsAgreed={isTermsAgreed}
			onTermsChange={onTermsChange}
		/>
	</>
);

type EndorsementFormProps = {
	title: string;
	backButtonAriaLabel: string;
	defaultValues: EndorsementFormData;
	isSubmitting: boolean;
	isHelpModalOpen: boolean;
	isSubmitModalOpen: boolean;
	onBack: () => void;
	onSubmit: (data: EndorsementFormSchema) => void | Promise<void>;
	onInvalidSubmit?: () => void;
	onHelpClick: () => void;
	onCloseHelpModal: () => void;
	onCloseSubmitModal: () => void;
	onConfirmSubmit: () => void | Promise<void>;
	pageContainer: string;
	contentContainer: string;
	formSection: string;
	previewSection: string;
	testId?: string;
	uppy: Uppy<Meta, Body>;
	image: string | undefined;
	uploadErrorMessage?: TranslationKey | null;
	isTermsAgreed: boolean;
	onTermsChange: (checked: boolean) => void;
	crop?: RawMediaCropInput;
	uploader?: UploaderMethodProps;
	onImageCrop?: (imageCrop: RawMediaCropInput | undefined) => void;
	croppedPreviewUrl?: string;
	onCroppedPreviewChange?: (url: string) => void;
};

export const EndorsementForm: React.FC<EndorsementFormProps> = ({
	title,
	backButtonAriaLabel,
	defaultValues,
	isSubmitting,
	isHelpModalOpen,
	isSubmitModalOpen,
	onBack,
	onSubmit,
	onInvalidSubmit,
	onHelpClick,
	onCloseHelpModal,
	onCloseSubmitModal,
	onConfirmSubmit,
	pageContainer,
	contentContainer,
	formSection,
	previewSection,
	testId,
	uppy,
	image,
	uploadErrorMessage,
	isTermsAgreed,
	onTermsChange,
	crop,
	uploader,
	onImageCrop,
	croppedPreviewUrl,
	onCroppedPreviewChange,
}) => {
	const { focusedField, handleFieldFocus, handleFieldBlur } = useEndorsementFocus();

	return (
		<Form
			schema={endorsementFormSchema}
			defaultValues={defaultValues}
			onSubmit={onSubmit}
			className={pageContainer}
			data-testid={testId}
			data-qa="endorsement-form"
		>
			{({ watch, handleSubmit, setValue }) => {
				const formData = watch();
				const statementCharCount = (formData.statement || '').length;

				const handleFormSubmit = async () => {
					// Sync photoUrl field with image state before validation
					setValue('photoUrl', image || '', { shouldValidate: false });

					await handleSubmit(onSubmit, () => {
						// Called when validation fails
						onInvalidSubmit?.();
					})();
				};

				return (
					<>
						<EndorsementFormHeader onBack={onBack} title={title} backButtonAriaLabel={backButtonAriaLabel} />
						<main className={contentContainer}>
							<div className={formSection}>
								<EndorsementFormFields
									statementCharCount={statementCharCount}
									uppy={uppy}
									image={image}
									onFieldFocus={handleFieldFocus}
									onFieldBlur={handleFieldBlur}
									uploadErrorMessage={uploadErrorMessage}
									crop={crop}
									uploader={uploader}
									onImageCrop={onImageCrop}
									onCroppedPreviewChange={onCroppedPreviewChange}
									onHelpClick={onHelpClick}
								/>
							</div>
							<PreviewSection
								formData={formData}
								image={image}
								croppedPreviewUrl={croppedPreviewUrl}
								focusedField={focusedField}
								previewSection={previewSection}
								onHelpClick={onHelpClick}
							/>
						</main>
						<EndorsementFormFooter onClick={handleFormSubmit} data-qa="endorsement-form-footer" />
						<Modals
							isHelpModalOpen={isHelpModalOpen}
							isSubmitModalOpen={isSubmitModalOpen}
							isSubmitting={isSubmitting}
							onCloseHelpModal={onCloseHelpModal}
							onCloseSubmitModal={onCloseSubmitModal}
							onConfirmSubmit={onConfirmSubmit}
							formData={formData}
							isTermsAgreed={isTermsAgreed}
							onTermsChange={onTermsChange}
							croppedPreviewUrl={croppedPreviewUrl}
						/>
					</>
				);
			}}
		</Form>
	);
};
